import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  User,
  StickyNote,
  Tag,
  Building2,
  MapPin,
  UserPlus,
  Pencil,
  ChevronRight,
} from "lucide-react";

function initials(name: string | null) {
  if (!name) return "?";
  return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
}

/**
 * O contato veio do webhook e ninguém completou o cadastro?
 *
 * Todo inbound cria a linha com `name` = o próprio telefone, então "nome igual
 * ao número (ou só dígitos)" é o sinal de cadastro cru. Serve só para destacar
 * o botão de editar — não bloqueia nada.
 */
function cadastroCru(name: string | null) {
  if (!name?.trim()) return true;
  // Nenhuma letra = ainda é o número, não um nome.
  return !/\p{L}/u.test(name);
}

export default function ContactPanel({
  contactId,
  contactPhone,
  contactName,
  onAddContact,
  onEditContact,
}: {
  contactId: number;
  contactPhone: string | null;
  contactName: string | null;
  onAddContact: () => void;
  onEditContact: () => void;
}) {
  const { data: contact, isLoading } = trpc.contacts.get.useQuery({ id: contactId });
  const { data: contactTags = [] } = trpc.contacts.getTags.useQuery({ contactId });

  if (isLoading) {
    return (
      <div className="flex-1 flex flex-col bg-card">
        <div className="p-4 flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary" />
        </div>
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="flex-1 flex flex-col bg-card">
        <div className="flex-1 flex flex-col items-center justify-center p-6 text-center gap-4">
          <div className="w-14 h-14 rounded-full bg-muted flex items-center justify-center">
            <User className="w-7 h-7 text-muted-foreground" />
          </div>
          <div>
            <p className="font-medium text-sm">{contactName ?? contactPhone ?? "Desconhecido"}</p>
            {contactPhone && <p className="text-xs text-muted-foreground font-mono mt-0.5">{contactPhone}</p>}
            <p className="text-xs text-muted-foreground mt-2">Contato não cadastrado no sistema</p>
          </div>
          <Button size="sm" onClick={onAddContact} className="gap-2 w-full">
            <UserPlus className="w-4 h-4" />
            Adicionar Contato
          </Button>
        </div>
      </div>
    );
  }

  const typeLabel: Record<string, string> = {
    public_admin: "Administração Pública",
    empresa: "Empresa",
    cliente_final: "Cliente Final",
  };

  return (
    <div className="flex-1 flex flex-col bg-card overflow-y-auto">
      {/* Avatar e nome */}
      <div className="p-4 flex flex-col items-center text-center border-b border-border">
        <Avatar className="w-14 h-14 mb-3">
          <AvatarFallback className="text-lg bg-primary/10 text-primary font-bold">
            {initials(contact.name)}
          </AvatarFallback>
        </Avatar>
        <p className="font-semibold text-sm">{contact.name}</p>
        {contact.phoneNumber && (
          <p className="text-xs text-muted-foreground font-mono mt-0.5">{contact.phoneNumber}</p>
        )}
        {contact.email && <p className="text-xs text-muted-foreground mt-0.5">{contact.email}</p>}
        {contact.optedOut && (
          <Badge variant="destructive" className="text-xs mt-2">Opt-out</Badge>
        )}

        {/* Editar é sempre a ação principal daqui: o contato do inbound já
            existe (nome = telefone), então o antigo "Adicionar Contato" — que
            só aparecia quando o contato NÃO existia — nunca era alcançado. */}
        <Button
          size="sm"
          variant={cadastroCru(contact.name) ? "default" : "outline"}
          onClick={onEditContact}
          className="gap-2 w-full mt-3"
        >
          <Pencil className="w-3.5 h-3.5" />
          {cadastroCru(contact.name) ? "Completar cadastro" : "Editar contato"}
        </Button>
        {cadastroCru(contact.name) && (
          <p className="text-xs text-muted-foreground mt-2">
            Este contato só tem o número. Preencha nome, órgão e cargo.
          </p>
        )}
      </div>

      {/* Informações */}
      <div className="p-4 space-y-3 text-sm">
        {(contact as any).contactType && (
          <div className="flex items-start gap-2">
            <User className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Tipo</p>
              <p className="font-medium text-xs">{typeLabel[(contact as any).contactType] ?? (contact as any).contactType}</p>
            </div>
          </div>
        )}

        {contact.companyName && (
          <div className="flex items-start gap-2">
            <Building2 className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">
                {(contact as any).contactType === "public_admin" ? "Órgão" : "Empresa"}
              </p>
              <p className="font-medium text-xs">{contact.companyName}</p>
              {contact.companyType && (
                <p className="text-xs text-muted-foreground">{contact.companyType}</p>
              )}
            </div>
          </div>
        )}

        {(contact as any).sector && (
          <div className="flex items-start gap-2">
            <ChevronRight className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Setor</p>
              <p className="font-medium text-xs">{(contact as any).sector}</p>
            </div>
          </div>
        )}

        {(contact.city || contact.state) && (
          <div className="flex items-start gap-2">
            <MapPin className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Localização</p>
              <p className="font-medium text-xs">{[contact.city, contact.state].filter(Boolean).join(" - ")}</p>
            </div>
          </div>
        )}

        {contact.notes && (
          <div className="flex items-start gap-2">
            <StickyNote className="w-3.5 h-3.5 text-muted-foreground mt-0.5 shrink-0" />
            <div>
              <p className="text-xs text-muted-foreground">Observações</p>
              <p className="text-xs">{contact.notes}</p>
            </div>
          </div>
        )}

        {/* Tags */}
        {contactTags.length > 0 && (
          <div>
            <p className="text-xs text-muted-foreground mb-1.5 flex items-center gap-1">
              <Tag className="w-3 h-3" /> Tags
            </p>
            <div className="flex flex-wrap gap-1">
              {contactTags.map((t: any) => (
                <Badge
                  key={t.id}
                  className="text-xs px-1.5 py-0.5"
                  style={{ backgroundColor: t.color, color: "white" }}
                >
                  {t.name}
                </Badge>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
